import React, { useState } from "react";
import { TableCell, TableRow, Typography, Stack, Button, Avatar, Box, Chip, IconButton, Tooltip } from "@mui/material";
import {
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Payment as PaymentIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";

import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { useUpdateOrderStatusMutation } from "../../../store/orders/ordersApis";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import { baseUrl } from "../../../helpers/constants";

const OrdersTableComponent = ({
    loading,
    fetching = false,
    count,
    data,
    onViewDetails,
    onStatusUpdate,
    pagination,
    handlePagination,
}) => {
    const [handleUpdateStatus] = useUpdateOrderStatusMutation();
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [orderToUpdate, setOrderToUpdate] = useState(null);
    const [newStatus, setNewStatus] = useState(null);

    const handleStatusChange = (order, status) => {
        setOrderToUpdate(order);
        setNewStatus(status);
        setConfirmationOpen(true);
    };

    const handleConfirmStatusChange = () => {
        if (orderToUpdate && newStatus) {
            handleUpdateStatus({
                order_id: orderToUpdate.id,
                payment_status: newStatus,
                notes: `Status changed to ${newStatus} by admin`,
            });
        }
        setConfirmationOpen(false);
        setOrderToUpdate(null);
        setNewStatus(null);
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
        setOrderToUpdate(null);
        setNewStatus(null);
    };

    // Helper to safely format price
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") {
            return "-";
        }
        const num = Number(price);
        if (isNaN(num)) {
            return "-";
        }
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Get status color
    const getStatusColor = (status) => {
        const statusColors = {
            pending: { bg: "#fff3cd", color: "#856404" },
            initiated: { bg: "#cce5ff", color: "#004085" },
            partial: { bg: "#ffeaa7", color: "#6c5ce7" },
            paid: { bg: "#d4edda", color: "#155724" },
            failed: { bg: "#f8d7da", color: "#721c24" },
            cancelled: { bg: "#f5c6cb", color: "#721c24" },
            refunded: { bg: "#e2e3e5", color: "#383d41" },
        };
        return statusColors[status] || { bg: "#f8f9fa", color: "#6c757d" };
    };

    // Get payment type color
    const getPaymentTypeColor = (type) => {
        return type === "full" ? { bg: "#d1ecf1", color: "#0c5460" } : { bg: "#f8d7da", color: "#721c24" };
    };

    let columns = [
        {
            title: "Order #",
            field: "order_number",
            sort: true,
            width: "12%",
            align: "left",
        },
        {
            title: "Customer",
            field: "customer",
            sort: false,
            width: "18%",
            align: "left",
        },
        {
            title: "Product",
            field: "product",
            sort: false,
            width: "15%",
            align: "left",
        },
        {
            title: "Amount",
            field: "total_amount",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "Payment Status",
            field: "payment_status",
            sort: true,
            width: "12%",
            align: "center",
        },
        {
            title: "Payment Type",
            field: "payment_type",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "Staff",
            field: "staff",
            sort: false,
            width: "10%",
            align: "center",
        },
        {
            title: "Created",
            field: "created_at",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "",
            name: "",
            sort: false,
            width: "3%",
            align: "center",
        },
    ];

    const renderTableData =
        !loading && data && data.length > 0
            ? data.map(function (row, index) {
                  const statusColor = getStatusColor(row.payment_status);
                  const paymentTypeColor = getPaymentTypeColor(row.payment_type);

                  return (
                      <TableRow
                          key={index}
                          sx={{
                              "td, th": {
                                  border: 0,
                                  padding: "16px 12px",
                                  verticalAlign: "middle",
                              },
                              "&:hover": {
                                  backgroundColor: "#f8f9fa",
                              },
                              transition: "all 0.2s ease-in-out",
                          }}
                      >
                          <TableCell align="left">
                              <Stack spacing={0.5}>
                                  <Typography
                                      variant="body2"
                                      sx={{
                                          fontWeight: 700,
                                          color: "#1976d2",
                                          fontSize: "0.875rem",
                                      }}
                                  >
                                      {row.order_number}
                                  </Typography>
                                  {row.notes && (
                                      <Typography
                                          variant="caption"
                                          sx={{
                                              color: "text.secondary",
                                              fontSize: "0.7rem",
                                              fontStyle: "italic",
                                          }}
                                      >
                                          {row.notes.length > 30 ? `${row.notes.substring(0, 30)}...` : row.notes}
                                      </Typography>
                                  )}
                              </Stack>
                          </TableCell>
                          <TableCell align="left">
                              <Stack spacing={0.5}>
                                  <Typography
                                      variant="body2"
                                      sx={{
                                          fontWeight: 600,
                                          color: "#2c3e50",
                                          fontSize: "0.875rem",
                                      }}
                                  >
                                      {row.customer?.name || "N/A"}
                                  </Typography>
                                  <Typography
                                      variant="caption"
                                      sx={{
                                          color: "text.secondary",
                                          fontSize: "0.75rem",
                                      }}
                                  >
                                      {row.customer?.mobile || "N/A"}
                                  </Typography>
                                  {row.customer?.city && (
                                      <Typography
                                          variant="caption"
                                          sx={{
                                              color: "text.secondary",
                                              fontSize: "0.7rem",
                                          }}
                                      >
                                          {row.customer.city}, {row.customer.state}
                                      </Typography>
                                  )}
                              </Stack>
                          </TableCell>
                          <TableCell align="left">
                              <Stack direction="row" spacing={1} alignItems="center">
                                  {row.product?.image_url && (
                                      <Avatar
                                          src={baseUrl + row.product.image_url}
                                          alt={row.product.name}
                                          sx={{
                                              width: 32,
                                              height: 32,
                                              border: "1px solid #e0e0e0",
                                          }}
                                      />
                                  )}
                                  <Box>
                                      <Typography
                                          variant="body2"
                                          sx={{
                                              fontWeight: 600,
                                              color: "#2c3e50",
                                              fontSize: "0.8rem",
                                          }}
                                      >
                                          {row.product?.name || "N/A"}
                                      </Typography>
                                      <Typography
                                          variant="caption"
                                          sx={{
                                              color: "text.secondary",
                                              fontSize: "0.7rem",
                                          }}
                                      >
                                          ₹{formatPrice(row.product?.price)}
                                      </Typography>
                                  </Box>
                              </Stack>
                          </TableCell>
                          <TableCell align="center">
                              <Stack spacing={0.5}>
                                  <Typography
                                      variant="body2"
                                      sx={{
                                          fontWeight: 700,
                                          color: "#2e7d32",
                                          fontSize: "0.875rem",
                                      }}
                                  >
                                      ₹{formatPrice(row.total_amount)}
                                  </Typography>
                                  {row.paid_amount > 0 && (
                                      <Typography
                                          variant="caption"
                                          sx={{
                                              color: "#1976d2",
                                              fontSize: "0.7rem",
                                          }}
                                      >
                                          Paid: ₹{formatPrice(row.paid_amount)}
                                      </Typography>
                                  )}
                                  {row.remaining_amount > 0 && (
                                      <Typography
                                          variant="caption"
                                          sx={{
                                              color: "#d32f2f",
                                              fontSize: "0.7rem",
                                          }}
                                      >
                                          Due: ₹{formatPrice(row.remaining_amount)}
                                      </Typography>
                                  )}
                              </Stack>
                          </TableCell>
                          <TableCell align="center">
                              <Chip
                                  label={row.payment_status?.toUpperCase() || "N/A"}
                                  size="small"
                                  sx={{
                                      backgroundColor: statusColor.bg,
                                      color: statusColor.color,
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 24,
                                      textTransform: "uppercase",
                                  }}
                              />
                          </TableCell>
                          <TableCell align="center">
                              <Chip
                                  label={row.payment_type?.toUpperCase() || "N/A"}
                                  size="small"
                                  sx={{
                                      backgroundColor: paymentTypeColor.bg,
                                      color: paymentTypeColor.color,
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 24,
                                      textTransform: "uppercase",
                                  }}
                              />
                          </TableCell>
                          <TableCell align="center">
                              <Stack spacing={0.5} alignItems="center">
                                  <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                  <Typography
                                      variant="caption"
                                      sx={{
                                          color: "text.secondary",
                                          fontSize: "0.7rem",
                                          textAlign: "center",
                                      }}
                                  >
                                      {row.staff?.name || "N/A"}
                                  </Typography>
                              </Stack>
                          </TableCell>
                          <TableCell align="center">
                              <Stack spacing={0.5} alignItems="center">
                                  <Typography
                                      variant="caption"
                                      sx={{
                                          color: "text.secondary",
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                      }}
                                  >
                                      {handleDateTimeDefault(row.created_at)}
                                  </Typography>
                                  {row.next_payment_date && (
                                      <Typography
                                          variant="caption"
                                          sx={{
                                              color: "#ff9800",
                                              fontSize: "0.7rem",
                                              fontWeight: 600,
                                          }}
                                      >
                                          Next: {new Date(row.next_payment_date).toLocaleDateString()}
                                      </Typography>
                                  )}
                              </Stack>
                          </TableCell>
                          <TableCell align="center">
                              <ActionMenu
                                  actions={[
                                      {
                                          ...ActionTypes.VIEW,
                                          onClick: () => onViewDetails(row),
                                      },
                                      {
                                          label: "Update Status",
                                          icon: <EditIcon />,
                                          onClick: () => onStatusUpdate(row),
                                      },
                                  ]}
                                  row={row}
                                  showStatusToggle={false}
                              />
                          </TableCell>
                      </TableRow>
                  );
              })
            : null;

    return (
        <>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                loading={loading}
                fetching={fetching}
                count={count}
                pagination={pagination}
                handlePagination={handlePagination}
            />
            <ConfirmationDialog
                open={confirmationOpen}
                title={`Update Order Status`}
                content={`Are you sure you want to change the status of order ${
                    orderToUpdate?.order_number
                } to ${newStatus?.toUpperCase()}?`}
                confirmButtonTitle="Yes, Update Status"
                onConfirm={handleConfirmStatusChange}
                onClose={handleCloseConfirmation}
            />
        </>
    );
};

export default OrdersTableComponent;
